import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    FiEdit,
    FiTrash2,
    FiDownload,
    FiInbox,
    FiArrowUpCircle,
    FiArrowDownCircle,
} from "react-icons/fi";

const API_BASE = "https://api.dreamtoday.in/api/admin";
const PER_PAGE = 5;

const UserTransactions = () => {
    const { id } = useParams();

    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);

    const [editOpen, setEditOpen] = useState(false);
    const [selectedTxn, setSelectedTxn] = useState(null);

    const [form, setForm] = useState({
        amount: "",
        reason: "",
        adminNote: "",
        status: "completed",
    });

    const [errors, setErrors] = useState({});

    // ✅ Fetch
    useEffect(() => {
        fetch(`${API_BASE}/user/${id}`)
            .then((res) => res.json())
            .then((data) => setTransactions(data.transactions || []))
            .finally(() => setLoading(false));
    }, [id]);

    // ✅ Smooth scroll on page change
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [page]);

    // ✅ Sort
    const sorted = [...transactions].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );

    const totalPages = Math.ceil(sorted.length / PER_PAGE);

    const paginated = sorted.slice(
        (page - 1) * PER_PAGE,
        page * PER_PAGE
    );

    // ✅ Ellipsis Pagination Logic
    const getPagination = () => {
        const pages = [];
        const maxVisible = 5;

        if (totalPages <= maxVisible) {
            return [...Array(totalPages)].map((_, i) => i + 1);
        }

        const start = Math.max(2, page - 1);
        const end = Math.min(totalPages - 1, page + 1);

        pages.push(1);

        if (start > 2) pages.push("...");

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        if (end < totalPages - 1) pages.push("...");

        pages.push(totalPages);

        return pages;
    };

    // ✅ Validation
    const validate = () => {
        let err = {};
        if (!form.amount || form.amount <= 0)
            err.amount = "Amount must be greater than 0";
        if (!form.reason.trim())
            err.reason = "Reason is required";

        setErrors(err);
        return Object.keys(err).length === 0;
    };

    // ✅ Delete
    const handleDelete = async (txnId) => {
        if (!window.confirm("Delete this transaction?")) return;

        await fetch(`${API_BASE}/transaction/${txnId}`, {
            method: "DELETE",
        });

        setTransactions((prev) => prev.filter((t) => t._id !== txnId));
    };

    // ✅ Edit
    const openEdit = (txn) => {
        setSelectedTxn(txn);
        setForm({
            amount: txn.amount,
            reason: txn.reason,
            adminNote: txn.adminNote,
            status: txn.status,
        });
        setErrors({});
        setEditOpen(true);
    };

    // ✅ Update
    const handleUpdate = async () => {
        if (!validate()) return;

        const res = await fetch(
            `${API_BASE}/transaction/${selectedTxn._id}`,
            {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            }
        );

        const data = await res.json();

        if (data.success) {
            setTransactions((prev) =>
                prev.map((t) =>
                    t._id === selectedTxn._id ? { ...t, ...form } : t
                )
            );
            setEditOpen(false);
        }
    };

    // ✅ Export CSV
    const exportCSV = () => {
        if (!transactions.length) return;

        const rows = [
            ["S.No", "Type", "Amount", "Reason", "Status", "Date"],
            ...transactions.map((t, i) => [
                i + 1,
                t.type,
                t.amount,
                t.reason,
                t.status,
                new Date(t.createdAt).toLocaleString(),
            ]),
        ];

        const csv = rows.map((r) => r.join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "transactions.csv";
        link.click();
    };

    return (
        <div className="p-4 sm:p-6 max-w-7xl mx-auto">

            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl sm:text-2xl font-bold">Transactions</h2>

                <button
                    onClick={exportCSV}
                    disabled={!transactions.length}
                    className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:scale-105 transition disabled:opacity-50"
                >
                    <FiDownload /> Export
                </button>
            </div>

            {/* Loading */}
            {loading && <p className="text-center py-10">Loading...</p>}

            {/* Empty */}
            {!loading && !transactions.length && (
                <div className="text-center py-16 text-gray-400">
                    <FiInbox size={50} className="mx-auto mb-3" />
                    <p className="text-lg font-semibold">No Transactions Available</p>
                </div>
            )}

            {/* Table */}
            {!loading && transactions.length > 0 && (
                <>
                    <div className="hidden sm:block overflow-x-auto border rounded-xl shadow">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="p-3">#</th>
                                    <th className="p-3">Type</th>
                                    <th className="p-3">Amount</th>
                                    <th className="p-3">Reason</th>
                                    <th className="p-3">Status</th>
                                    <th className="p-3">Date</th>
                                    <th className="p-3">Actions</th>
                                </tr>
                            </thead>

                            <tbody>
                                {paginated.map((txn, i) => {
                                    const isCredit = txn.type === "credit";

                                    return (
                                        <tr key={txn._id} className="border-t hover:bg-gray-50">
                                            <td className="p-3 text-center">
                                                {(page - 1) * PER_PAGE + i + 1}
                                            </td>

                                            <td className="p-3 flex items-center gap-2">
                                                {isCredit ? (
                                                    <FiArrowUpCircle className="text-green-600" />
                                                ) : (
                                                    <FiArrowDownCircle className="text-red-500" />
                                                )}
                                                {txn.type}
                                            </td>

                                            <td className={`p-3 font-semibold ${isCredit ? "text-green-600" : "text-red-500"}`}>
                                                {isCredit ? "+" : "-"} {txn.amount}
                                            </td>

                                            <td className="p-3">{txn.reason}</td>

                                            <td className="p-3">
                                                <span className="px-2 py-1 text-xs bg-blue-100 rounded">
                                                    {txn.status}
                                                </span>
                                            </td>

                                            <td className="p-3 text-xs text-gray-500">
                                                {new Date(txn.createdAt).toLocaleString()}
                                            </td>

                                            <td className="p-3 flex gap-2 justify-center">
                                                <button onClick={() => openEdit(txn)}>
                                                    <FiEdit className="text-blue-600" />
                                                </button>
                                                {/* <button onClick={() => handleDelete(txn._id)}>
                          <FiTrash2 className="text-red-600" />
                        </button> */}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="flex justify-center gap-2 mt-6 flex-wrap">
                        <button
                            onClick={() => setPage((p) => Math.max(p - 1, 1))}
                            disabled={page === 1}
                            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-40"
                        >
                            Prev
                        </button>

                        {getPagination().map((p, i) =>
                            p === "..." ? (
                                <span key={i} className="px-2 text-gray-400">...</span>
                            ) : (
                                <button
                                    key={i}
                                    onClick={() => setPage(p)}
                                    className={`px-3 py-1 rounded ${page === p ? "bg-blue-600 text-white" : "bg-gray-200"
                                        }`}
                                >
                                    {p}
                                </button>
                            )
                        )}

                        <button
                            onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                            disabled={page === totalPages}
                            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-40"
                        >
                            Next
                        </button>
                    </div>
                </>
            )}

            {/* 🔥 EDIT MODAL */}
            {editOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/50 backdrop-blur-sm">

                    <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl 
      max-h-[95vh] flex flex-col overflow-hidden">

                        {/* Header */}
                        <div className="p-4 sm:p-6 border-b flex justify-between items-center">
                            <h3 className="text-lg sm:text-xl font-bold">
                                Edit Transaction
                            </h3>

                            <button
                                onClick={() => setEditOpen(false)}
                                className="p-2 hover:bg-gray-100 rounded-lg"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Body */}
                        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">

                            {/* Amount */}
                            <div>
                                <label className="text-sm font-medium">Amount</label>
                                <input
                                    type="number"
                                    value={form.amount}
                                    onChange={(e) =>
                                        setForm({ ...form, amount: e.target.value })
                                    }
                                    className="w-full mt-1 px-3 py-2 border rounded-lg"
                                />
                                {errors.amount && (
                                    <p className="text-red-500 text-xs mt-1">
                                        {errors.amount}
                                    </p>
                                )}
                            </div>

                            {/* Reason */}
                            <div>
                                <label className="text-sm font-medium">Reason</label>
                                <input
                                    value={form.reason}
                                    onChange={(e) =>
                                        setForm({ ...form, reason: e.target.value })
                                    }
                                    className="w-full mt-1 px-3 py-2 border rounded-lg"
                                />
                                {errors.reason && (
                                    <p className="text-red-500 text-xs mt-1">
                                        {errors.reason}
                                    </p>
                                )}
                            </div>

                            {/* Admin Note */}
                            <div>
                                <label className="text-sm font-medium">Admin Note</label>
                                <textarea
                                    value={form.adminNote}
                                    onChange={(e) =>
                                        setForm({ ...form, adminNote: e.target.value })
                                    }
                                    rows="3"
                                    className="w-full mt-1 px-3 py-2 border rounded-lg resize-none"
                                />
                            </div>

                            {/* Status */}
                            <div>
                                <label className="text-sm font-medium">Status</label>
                                <select
                                    value={form.status}
                                    onChange={(e) =>
                                        setForm({ ...form, status: e.target.value })
                                    }
                                    className="w-full mt-1 px-3 py-2 border rounded-lg"
                                >
                                    <option value="completed">Completed</option>
                                    <option value="pending">Pending</option>
                                </select>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t flex flex-col sm:flex-row gap-2">
                            <button
                                onClick={handleUpdate}
                                className="flex-1 bg-blue-600 text-white py-2.5 rounded-lg hover:bg-blue-700 transition"
                            >
                                Update
                            </button>

                            <button
                                onClick={() => setEditOpen(false)}
                                className="flex-1 bg-gray-100 py-2.5 rounded-lg"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserTransactions;